import React, { useCallback, useEffect, useState, FC } from 'react';
import { AnalyticsEventLocation } from '@mb/lib/analytics';
import { CustomIcon } from 'components/CustomIcon';
import AutoScroll, { AutoScrollType } from 'embla-carousel-auto-scroll';
import useEmblaCarousel from 'embla-carousel-react';

import { useInView } from 'react-intersection-observer';
import Arrow, { ArrowDirection } from './Arrow';
import * as Styled from './CollectionCarousel.styles';
import { CollectionItem } from './CollectionItem';
import { PauseActionIconButton, PlayActionIconButton } from './ScrollAction';
import { ScrollingCollectionCarouselProps } from './types';
import { goToPageTracking } from '../../helpers';
import {
  CarouselAction,
  sendCarouselAnalyticsEvent,
  sendSeeAllAnalyticsEvent,
} from '../../helpers/analitycs';
import useInViewAnalytics from '../../hooks/useInViewAnalytics';
import { useSeeAllData } from '../../hooks/useSeeAllData';

export const MAX_SCROLLING_ASSETS = 25;
export const MAX_DISPLAYED_ASSETS = 4;
export const CUSTOM_ITEM_WIDTH = 274;

export const ScrollingCollectionCarousel: FC<
  ScrollingCollectionCarouselProps
> = (props) => {
  const {
    title,
    isFeatured,
    campaigns,
    onSeeAllClick,
    isLoading = true,
    actionLabel = 'See all',
    eventLocation,
    ctaPosition = 'right',
    autoStart = false,
    slideLimit,
    isWide = false,
  } = props;
  if (campaigns.length === 0) return null;

  const allAssetsVisible = useCallback(() => {
    return campaigns.length <= MAX_DISPLAYED_ASSETS;
  }, [campaigns]);

  const [isInView, setIsInView] = useState(false);
  const [slidesInView, setSlidesInView] = useState(new Set<number>());
  const { ref: inViewRef } = useInView({
    threshold: 0.5,
    onChange: setIsInView,
  });
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(false);

  const [isPlaying, setIsPlaying] = useState(autoStart);
  const [isPaused, setIsPaused] = useState(false);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      dragFree: true,
      slidesToScroll: 'auto',
      loop: autoStart,
    },
    [
      AutoScroll({
        playOnInit: autoStart && !allAssetsVisible(),
        stopOnMouseEnter: true,
        stopOnFocusIn: true,
        stopOnInteraction: true,
        speed: 0.5,
        startDelay: 0,
      }),
    ],
  );

  const { contentId, contentType } = useSeeAllData(title);
  const { handleInViewChange } = useInViewAnalytics({
    campaigns,
    title,
    isInView,
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, [emblaApi]);

  const updateSlidesInView = useCallback(() => {
    if (!emblaApi) return;
    if (slidesInView.size === emblaApi.slideNodes().length) {
      emblaApi.off('slidesInView', updateSlidesInView);
    }
    setSlidesInView(new Set([...emblaApi.slidesInView()]));
  }, [emblaApi]);

  const handlePauseClick = useCallback(() => {
    const autoScroll: AutoScrollType | undefined =
      emblaApi?.plugins()?.autoScroll;
    if (!autoScroll) return;

    if (autoScroll.isPlaying()) {
      autoScroll.stop();
      setIsPlaying(false);
    }
    setIsPaused(true);
  }, [emblaApi, setIsPlaying, setIsPaused]);

  const handlePlayClick = useCallback(() => {
    const autoScroll: AutoScrollType | undefined =
      emblaApi?.plugins()?.autoScroll;
    if (!autoScroll) return;

    if (!autoScroll.isPlaying()) {
      autoScroll.play();
      setIsPlaying(true);
    }
    setIsPaused(false);
  }, [emblaApi, setIsPlaying, setIsPaused]);

  const handleMouseEnter = useCallback(() => {
    if (!autoStart || allAssetsVisible()) return;

    const autoScroll: AutoScrollType | undefined =
      emblaApi?.plugins()?.autoScroll;
    if (!autoScroll) return;

    if (autoScroll.isPlaying()) {
      autoScroll.stop();
      setIsPlaying(false);
    }
  }, [emblaApi, setIsPlaying, allAssetsVisible]);

  const handleMouseLeave = useCallback(() => {
    if (!autoStart || allAssetsVisible()) return;

    const autoScroll: AutoScrollType | undefined =
      emblaApi?.plugins()?.autoScroll;
    if (!autoScroll) return;

    if (!autoScroll.isPlaying() && !isPaused) {
      autoScroll.play();
      setIsPlaying(true);
    }
  }, [emblaApi, isPaused, setIsPlaying, allAssetsVisible]);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect();
    updateSlidesInView();
    emblaApi.on('slidesInView', updateSlidesInView);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('slidesInView', updateSlidesInView);
      emblaApi.off('reInit', onSelect);
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect, updateSlidesInView]);

  const scrollPrev = useCallback(() => {
    if (!emblaApi) return;

    sendCarouselAnalyticsEvent(title, CarouselAction.BACK, eventLocation);
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (!emblaApi) return;

    sendCarouselAnalyticsEvent(title, CarouselAction.FORWARD, eventLocation);
    emblaApi.scrollNext();
  }, [emblaApi]);

  const handleSeeAllClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      sendSeeAllAnalyticsEvent(title, eventLocation);
      if (eventLocation === AnalyticsEventLocation.HOME) {
        goToPageTracking('collections');
      }
      onSeeAllClick();
    },
    [onSeeAllClick, title],
  );

  return (
    <Styled.Block
      ref={inViewRef}
      ctaPosition={ctaPosition}
      isHomePage={eventLocation === AnalyticsEventLocation.HOME}
    >
      <Styled.Header
        ctaPosition={ctaPosition}
        isHomePage={eventLocation === AnalyticsEventLocation.HOME}
      >
        <Styled.Title
          variant="h3"
          fontWeight="bold"
          onClick={handleSeeAllClick}
        >
          {title}
        </Styled.Title>
        <Styled.LinkWrapper>
          <Styled.Link
            color="primary"
            onClick={handleSeeAllClick}
            id={contentId}
            title={contentType}
            alternateDesign={isWide}
          >
            {actionLabel}
            {isWide ? <CustomIcon name="arrow-right" /> : null}
          </Styled.Link>
        </Styled.LinkWrapper>
        {autoStart && !allAssetsVisible() && (
          <Styled.ScrollAction>
            {isPlaying ? (
              <PauseActionIconButton onClick={handlePauseClick} />
            ) : (
              <PlayActionIconButton onClick={handlePlayClick} />
            )}
          </Styled.ScrollAction>
        )}
      </Styled.Header>
      {isLoading || !slideLimit ? (
        <Styled.Wrapper>
          <Styled.Viewport>
            <Styled.Content>
              {Array.from({ length: MAX_DISPLAYED_ASSETS }).map((_, index) => (
                <CollectionItem
                  key={index}
                  campaign={null}
                  isFeatured={isFeatured}
                  collectionTitle=""
                  index={index}
                  inView={false}
                  eventLocation={eventLocation}
                  customWidth={CUSTOM_ITEM_WIDTH}
                />
              ))}
            </Styled.Content>
          </Styled.Viewport>
        </Styled.Wrapper>
      ) : (
        <Styled.Wrapper>
          {!allAssetsVisible() && (isPaused || !autoStart) && (
            <div className={`scrollButtons ${isWide ? 'scroll--wide' : ''}`}>
              <Arrow
                direction={ArrowDirection.LEFT}
                onClick={scrollPrev}
                show={!prevBtnDisabled}
              />
              <Arrow
                direction={ArrowDirection.RIGHT}
                onClick={scrollNext}
                show={!nextBtnDisabled}
              />
            </div>
          )}
          <Styled.Viewport
            ref={emblaRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <Styled.ScrollingContent>
              {campaigns.slice(0, slideLimit).map((campaign, index) => (
                <CollectionItem
                  key={campaign._id}
                  campaign={campaign}
                  isFeatured={isFeatured}
                  sendInViewChange={handleInViewChange}
                  collectionTitle={title}
                  index={index}
                  inView={slidesInView.has(index)}
                  eventLocation={eventLocation}
                  customWidth={CUSTOM_ITEM_WIDTH}
                />
              ))}
            </Styled.ScrollingContent>
          </Styled.Viewport>
        </Styled.Wrapper>
      )}
    </Styled.Block>
  );
};
